/*
 * © Lumile Argentina S.A.
 * Todos los derechos reservados.
 * Licencia Propietaria de Lumile Argentina S.A.
 *
 * Este código es propiedad de Lumile Argentina S.A. y fue desarrollado para proyectos propios
 * como parte de un framework de desarrollo creado por Lumile.
 *
 * Queda prohibida la reproducción, distribución o modificación sin autorización explícita de Lumile.
 *
 * Cualquier uso de este código en proyectos no autorizados debe ser aprobado previamente por Lumile.
 *
 * Este software y su código fuente son propiedad exclusiva de Lumile Argentina S.A., y están protegidos bajo los términos de esta Licencia Propietaria. La reproducción, modificación, distribución o cualquier uso no autorizado de este software queda prohibido sin el consentimiento expreso y por escrito de Lumile Argentina S.A.
 *
 * Esta licencia garantiza acceso al código para su revisión y uso interno exclusivamente como parte de los proyectos y servicios proporcionados por Lumile Argentina S.A., o en proyectos autorizados por escrito. Todos los derechos no expresamente otorgados en esta licencia están reservados a Lumile Argentina S.A.
 *
 * Contacto: comercial@lumile.com.ar
 */
import _ from 'lodash'
import moment from 'moment'

// Utils
import commonsApi from '@/api/commons'

// Mixins
import lmToasts from '@/mixins/lmToasts'

// Components
import LmFormHeader from '@/components/LmFormHeader.vue'
import { notification } from 'ant-design-vue'

export default {
  mixins: [lmToasts],
  components: {
    LmFormHeader
  },
  props: {
    isOnModal: {
      type: Boolean,
      default: false
    },
    frontendRouteToRedirectAfterCreated: {
      type: String
    },
    isOnEditMode: {
      type: Boolean,
      default: false
    },
    serviceEndpoint: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      fieldToFocus: null,
      isLoading: false,
      originalForm: {},
      actionsAvailables: {
        canBeCreatedByMe: false,
        canBeEditedByMe: false,
        canBeDeletedByMe: false
      },
      hasChanged: false,
      isProcessing: true
    }
  },

  computed: {
    iCanDoIt () {
      return this.actionsAvailables.canBeCreatedByMe || this.actionsAvailables.canBeEditedByMe
    }
  },

  watch: {
    form: {
      deep: true,
      handler (newVal) {
        if ((this.isOnEditMode && this.isLoading) || this.isOnModal) { return }

        // Función para comparar profundamente y ignorar ciertas propiedades
        const deepCompare = (obj1, obj2) => {
          const ignoreKeys = ['_id', 'id', 'createdAt', 'updatedAt']
          return _.isEqual(
            _.omit(obj1, ignoreKeys),
            _.omit(obj2, ignoreKeys)
          )
        }

        // Verifica si hay cambios significativos entre el nuevo y el viejo valor
        if (!deepCompare(newVal, this.originalForm)) {
          this.hasChanged = true
        } else {
          this.hasChanged = false
        }
      }
    }
  },

  methods: {
    v (position) {
      return _.get(_.cloneDeep(this.$v), position)
    },
    validateForm (validation) {
      validation.$touch()
      if (validation.$error) {
        this.lmToastDangerMessage(this.$t('form_with_errors'), 'Error')
        throw new Error('validation error')
      } else {
        if (validation.$pending || validation.$error) {
          throw new Error('validation error')
        }
      }
    },
    async getActionsAvailables () {
      const targetEndpoint = `/${this.serviceEndpoint}/actionsavailable/`
      const response = await commonsApi.axiosGetWrapper(targetEndpoint)
      this.actionsAvailables = response.data
    },
    async onSubmit (finishWithGoBack = true) {
      try {
        await this.validateForm(this.$v)
        this.isProcessing = true
        this.form.issueDate = moment(this.form.issueDate, this.dateFormat).toDate()

        let res
        if (this.isOnEditMode) {
          await commonsApi.axiosPutWrapper(`/${this.serviceEndpoint}/${this.$route.params.id}`, this.form)
        } else {
          res = await commonsApi.axiosPostWrapper(`/${this.serviceEndpoint}`, this.form)
        }
        this.hasChanged = false
        this.lmToastSuccessMessage(
          `${this.$t('saved_successfully')} '${this.form.name}'`,
          `${this.$t('action_done_successfully')}`
        )

        if (this.isOnModal) {
          this.$emit('itemCreated', {
            ...res.data,
            id: res.data._id
          })
          this.onClose()
        } else {
          if (finishWithGoBack) {
            this.onGoBack()
          } else if (!this.isOnEditMode) {
            if (this.frontendRouteToRedirectAfterCreated) {
              this.$router.replace(`${this.frontendRouteToRedirectAfterCreated}${res.data._id}`)
            } else {
              this.onGoBack()
            }
          }
        }
        this.hasChanged = false
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },
    onReactivateConfirmation () {
      this.lmModalInformationMessage({
        modalClosable: false,
        modalKeyboard: false,
        resultTitle: this.$t('you_are_about_to_reactivate'),
        resultSubtitle: this.$t('are_you_sure_to_reactivate'),
        buttons: [
          {
            btnText: this.$t('cancel'),
            btnType: 'danger',
            btnGhost: true,
            btnCallback: () => {
            }
          },
          {
            btnText: this.$t('accept'),
            btnCallback: () => {
              this.onReactivate()
            }
          }
        ]
      })
    },
    onDeleteConfirmation () {
      this.lmModalInformationMessage({
        modalClosable: false,
        modalKeyboard: false,
        resultTitle: this.$t('you_are_about_to_delete'),
        resultSubtitle: this.$t('are_you_sure_you_want_to_delete_this'),
        buttons: [
          {
            btnText: this.$t('cancel'),
            btnType: 'danger',
            btnGhost: true,
            btnCallback: () => {
            }
          },
          {
            btnText: this.$t('accept'),
            btnCallback: () => {
              this.onDelete()
            }
          }
        ]
      })
    },
    async onDelete () {
      try {
        this.isProcessing = true
        await commonsApi.axiosDeleteWrapper(`/${this.serviceEndpoint}/delete/${this.$route.params.id}`)
        this.lmToastSuccessMessage(
          `${this.$t('was_successfully_deleted')} ${this.form.name}`,
          `${this.$t('action_done_successfully')}`
        )
      } catch (error) {
        console.log(error)
      } finally {
        await this.loadData()
        this.isProcessing = false
        this.hasChanged = false
      }
    },
    onClose () {
      this.$emit('closeModal')
    },
    async onReactivate () {
      try {
        this.isProcessing = true
        await commonsApi.axiosPutWrapper(`/${this.serviceEndpoint}/reactivate/${this.$route?.params?.id}`)
        this.lmToastSuccessMessage(`${this.$t('was_successfully_reactivated')}`, `${this.$t('it_was_successfully_reactivated')}`)
      } catch (error) {
        console.log(error)
      } finally {
        await this.loadData()
        this.isProcessing = false
        this.hasChanged = false
      }
    },
    async loadData () {
      if (this.$route.params.id) {
        const res = await commonsApi.axiosGetWrapper(`/${this.serviceEndpoint}/${this.$route.params.id}`)
        this.form = res.data
      }
    },
    // Añade este método para detectar dispositivos móviles
    isMobileDevice () {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    },
    forceFocus () {
      if (this.fieldToFocus && !this.isMobileDevice()) {
        this.$nextTick(() => {
          this.$refs[this.fieldToFocus].forceFocus()
        })
      }
    },
    onGoBack () {
      if (this.hasChanged) {
        this.showUnsavedChangesModal({ path: this.$router.currentRoute.path }, () => {
          this.$router.go(-1) // Navega hacia atrás después de la confirmación
        })
      } else {
        this.$router.go(-1) // Navega hacia atrás directamente si no hay cambios
      }
    },
    async handleContinue (to, next) {
      this.hasChanged = false
      next() // Continúa la navegación
    },
    async handleSave (to, next) {
      try {
        await this.onSubmit(false) // Llama al método para guardar los cambios
        next() // Continúa la navegación
      } catch (error) {
        return false
      }
    },
    showUnsavedChangesModal (to, next) {
      this.lmModalWarningMessage({
        resultTitle: this.$t('changes_unsaved'),
        resultSubtitle: this.$t('changes_unsaved_subtitle'),
        buttons: [
          {
            btnText: this.$t('continue'),
            btnCallback: () => this.handleContinue(to, next)
          },
          {
            btnText: this.$t('save'),
            btnCallback: () => this.handleSave(to, next)
          }
        ]
      })
    }
  },
  beforeRouteLeave (to, from, next) {
    if (process.env.VUE_APP_SOCKET_ENABLED === 'yes' && this.$route.params.id) {
      this.sockets.unsubscribe(`onNews_${this.$route.params.id}`)
    }
    if (this.hasChanged) {
      this.showUnsavedChangesModal(to, next)
      return false
    }
    next()
  },
  mounted () {
    if (process.env.VUE_APP_SOCKET_ENABLED === 'yes') {
      const key = `open${Date.now()}`
      if (this.$route.params.id) {
        this.sockets.subscribe(`onNews_${this.$route.params.id}`, async _ => {
          notification.success({
            message: 'Hay novedades.',
            description:
          'Por favor, recarga para verlas.',
            duration: 0,
            btn: h => {
              return h(
                'a-button',
                {
                  props: {
                    type: 'primary',
                    size: 'small'
                  },
                  on: {
                    click: () => {
                      this.loadData()
                      notification.close(key)
                    }
                  }
                },
                this.$t('refresh')
              )
            },
            key,
            onClose: close
          })
        })
      }
    }
  },
  async created () {
    try {
      this.isLoading = true
      await this.getActionsAvailables()
      if (this.isOnEditMode) {
        await this.loadData()
      }
      this.isProcessing = false
    } catch (error) {
      console.log(error)
    } finally {
      this.originalForm = _.cloneDeep(this.form)
      this.isLoading = false
      // this.forceFocus()
    }
  }
}
